import { useTiming } from '@/hooks/useTiming';
import { resolutions } from '@/pages/AdvancedEditor/constants';
import { useAdvancedEditorContext } from '@/pages/AdvancedEditor/context/useAdvancedEditorContext';
import { useInitFromAvatarPage } from '@/pages/AdvancedEditor/hooks/polotno-store/store-init/useInitFromAvatarPage';
import { useInitNormalizedData } from '@/pages/AdvancedEditor/hooks/polotno-store/store-init/useInitNormalizedData';
import { useInitWithWalksThrough } from '@/pages/AdvancedEditor/hooks/polotno-store/store-init/useInitWithWalksThrough';
import { logger } from '@/services/logger';
import { CropMode } from '@/utils/types';
import { useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { getPageProperty, PolotnoStoreActivePageKeys } from '../usePolotnoActivePageState';
import { useGetPolotnoInitialData } from './useGetPolotnoInitialData';
import { useNormalizePolotnoInitialData } from './useNormalizePolotnoInitialData';

export const usePolotnoStoreInit = () => {
    const { store, debouncedSaveArtifacts, createThumbnails } = useAdvancedEditorContext();
    const { state: locationState } = useLocation();
    const { getInitialData } = useGetPolotnoInitialData();
    const { normalizeInitialData } = useNormalizePolotnoInitialData();
    const { initNormalizedData } = useInitNormalizedData();
    const { initFromAvatarsPage } = useInitFromAvatarPage();
    const { initWithWalksThrough } = useInitWithWalksThrough();
    const { withTiming } = useTiming();

    const createThumbnailsLazily = useCallback(() => {
        setTimeout(() => {
            createThumbnails(store)
                .then(() => debouncedSaveArtifacts(store))
                .catch(error => {
                    console.error('Failed to create thumbnails', error);
                    logger.error('Error during creating thumbnails:', error);
                });
        }, 1000);
    }, [createThumbnails, debouncedSaveArtifacts, store]);

    const initStore = useCallback(
        async (callback?: () => void) => {
            const initialData = await withTiming('useGetPolotnoInitialData.getInitialData', () => getInitialData(locationState?.fromIdb));
            const initialAvatar = locationState?.initialAvatar || null; // its used when we create new project from avatar page
            const initialTemplate = locationState?.template || null;

            // if we have field custom, it means its created using polotno
            // if template provided, use it first - highest priority
            const pageHasCustom = initialData?.rawData?.pages.some((page: any) => page.custom);
            if (initialTemplate || initialData?.rawData?.custom || pageHasCustom) {
                await withTiming('usePolotnoStoreInit.initStore case 1', async () => {
                    if (initialTemplate) {
                        try {
                            store.loadJSON(initialTemplate);
                        } catch (error) {
                            logger.error('Error during applying template data:', error);
                            throw error;
                        }
                    }

                    if (!initialTemplate && initialData?.rawData) {
                        store.loadJSON(initialData.rawData);
                    }

                    await store.waitLoading();
                    await debouncedSaveArtifacts(store);

                    if (store.pages.some(page => !getPageProperty(page, PolotnoStoreActivePageKeys.thumbnail))) {
                        createThumbnailsLazily();
                    }
                    store.history.clear();
                });

                callback?.();
                return;
            }

            await withTiming('usePolotnoStoreInit.initStore case 2', async () => {
                const normalizedData = await normalizeInitialData(initialData, initialAvatar);

                if (initialData?.rawData) {
                    store.loadJSON(initialData.rawData);
                }

                let page = store.pages?.[0];
                if (normalizedData && page) {
                    await initNormalizedData(normalizedData, page);
                }

                if (!store.pages.length) {
                    store.addPage(resolutions[CropMode.Wide]);
                    page = store.pages[0];
                }

                // when we create new project from avatar page
                if (initialAvatar) {
                    await initFromAvatarsPage(initialAvatar, page);
                }

                // when no initial data, but needsWalkthrough
                if ((locationState as any)?.loadWalkthrough) {
                    await initWithWalksThrough(page);
                }

                await store.waitLoading();
                if (store.pages.some(page => !getPageProperty(page, PolotnoStoreActivePageKeys.thumbnail))) {
                    createThumbnailsLazily();
                }

                await debouncedSaveArtifacts(store);
                store.history.clear();
            });

            callback?.();
        },
        [
            createThumbnailsLazily,
            debouncedSaveArtifacts,
            getInitialData,
            initFromAvatarsPage,
            initNormalizedData,
            initWithWalksThrough,
            locationState,
            normalizeInitialData,
            store,
            withTiming,
        ]
    );

    return { initStore };
};
